import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon, HeartIcon } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
// Image URLs
const IMAGES = {
  main: 'https://cdn.magicpatterns.com/uploads/fqYYJBDwFWoajEVsJGcgps/ChatGPT_Image_24_dic_2025,_12_12_35_p.m..png',
  bonus1: 'https://cdn.magicpatterns.com/uploads/qTgJEGiyeRiS4nBWDcu7yp/ChatGPT_Image_24_dic_2025,_12_44_02_p.m..png',
  bonus2: 'https://cdn.magicpatterns.com/uploads/qJ4CfAQkpiYMNVCUbM8AKN/ChatGPT_Image_24_dic_2025,_12_46_20_p.m..png',
  bonus3: 'https://cdn.magicpatterns.com/uploads/eHwykJ5uZS9w8hZUsoXhDQ/ChatGPT_Image_24_dic_2025,_12_50_24_p.m..png',
  bonus4: 'https://cdn.magicpatterns.com/uploads/fh8uQnYm6zunQTR9PkLh3H/ChatGPT_Image_24_dic_2025,_12_52_53_p.m..png'
};
const TESTIMONIALS = [{
  name: 'María González',
  age: 32,
  city: 'Buenos Aires',
  text: 'Llevaba años sintiéndome ansiosa todo el tiempo. Este libro me ayudó a entender que no estoy rota, solo necesitaba calmar mi sistema nervioso. Los ejercicios de 10 minutos realmente funcionan.'
}, {
  name: 'Laura Martínez',
  age: 28,
  city: 'Ciudad de México',
  text: 'Me despertaba agotada cada día. Pensé que era normal hasta que leí esto. Ahora tengo herramientas prácticas que uso cuando siento que mi mente no para.'
}, {
  name: 'Sofía Ramírez',
  age: 35,
  city: 'Bogotá',
  text: 'La culpa de no poder relajarme me consumía. Este libro me enseñó que mi cuerpo estaba protegiéndome. Cambió completamente mi perspectiva y mi vida.'
}, {
  name: 'Ana Torres',
  age: 41,
  city: 'Santiago',
  text: 'Finalmente entendí qué es el modo supervivencia. Los protocolos son simples pero poderosos. Por primera vez en años, siento que puedo respirar.'
}, {
  name: 'Valentina López',
  age: 29,
  city: 'Lima',
  text: 'Mi mente nunca se callaba. Probé de todo. Estos ejercicios cuerpo-mente fueron lo único que realmente me dio alivio inmediato.'
}];
const CHECKLIST_ITEMS = ['Tu mente no se calla', 'Tu cuerpo nunca termina de relajarse', 'Vivís en alerta aunque "todo esté bien"', 'Querés calma sin medicarte', 'Necesitás algo que funcione ahora, no en meses'];
export function SalesPage() {
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-purple-50 font-sans text-gray-800 overflow-x-hidden" data-id="element-16">
      <PurchaseNotification data-id="element-17" />

      {/* Hero Section */}
      <header className="relative pt-12 pb-16 px-4 md:pt-20 md:pb-24 overflow-hidden" data-id="element-18">
        <div className="max-w-4xl mx-auto text-center" data-id="element-19">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} className="mb-8 relative inline-block cursor-pointer group" onClick={scrollToPurchase} data-id="element-20">
            <div className="absolute inset-0 bg-orange-300 blur-3xl opacity-30 rounded-full transform scale-110 group-hover:scale-125 transition-transform duration-500" data-id="element-21"></div>
            <img src={IMAGES.main} alt="No Estás Rota: Estás en Modo Supervivencia" className="relative w-full max-w-md mx-auto rounded-lg shadow-2xl transform group-hover:-translate-y-2 transition-transform duration-300" data-id="element-22" />
          </motion.div>

          <motion.h1 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-23">
            No estás rota.
            <span className="block text-orange-600 mt-2" data-id="element-24">
              Estás en modo supervivencia.
            </span>
          </motion.h1>
        </div>
      </header>

      {/* Main Content Container */}
      <main className="max-w-3xl mx-auto px-6 pb-24 space-y-16" data-id="element-25">
        {/* Hook / Problem Agitation */}
        <section className="prose prose-lg mx-auto text-gray-700" data-id="element-26">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-27">
            <p className="lead text-xl md:text-2xl font-medium text-gray-800 mb-8" data-id="element-28">
              Y si alguien te hubiese explicado esto antes, te habrías ahorrado
              años de culpa, miedo y agotamiento.
            </p>
            <p data-id="element-29">
              Porque la ansiedad no aparece de la nada. No es debilidad. No es
              que “pensás demasiado”.
            </p>
            <p className="font-semibold text-purple-800 bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400" data-id="element-30">
              Es un cuerpo que aprendió que el mundo no siempre fue seguro… y
              ahora no sabe cómo apagar la alarma.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="mt-12" data-id="element-31">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-32">
              Quizás te pasa esto:
            </h3>
            <p data-id="element-33">
              Te despertás cansada, aunque dormiste. Tu cabeza empieza a correr
              antes de que pongas un pie en el piso. Durante el día funcionás.
              Cumplís. Respondés. Sostenés.
            </p>
            <p className="font-bold" data-id="element-34">Pero por dentro…</p>
            <p data-id="element-35">
              El pecho está apretado. La respiración es corta. El cuerpo nunca
              termina de relajarse. Y hay una sensación constante de que algo
              malo podría pasar… aunque no sepas qué.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="mt-12" data-id="element-36">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-37">
              Y lo peor no es la ansiedad.
            </h3>
            <p data-id="element-38">
              Lo peor es esa pregunta silenciosa que aparece cuando estás sola:
            </p>
            <p className="text-xl font-serif italic text-center my-8 text-gray-600" data-id="element-39">
              “¿Qué me pasa a mí?”
            </p>
            <p data-id="element-40">Déjame decirte algo con claridad:</p>
            <p className="text-lg font-medium text-orange-700" data-id="element-41">
              No sos el problema. Tu cuerpo está haciendo exactamente lo que
              aprendió a hacer para protegerte.
            </p>
            <p data-id="element-42">Y eso —aunque hoy te duela— es una buena noticia.</p>
            <p className="text-xl font-bold text-gray-900" data-id="element-43">
              Porque lo que se aprende… se puede desaprender.
            </p>
          </motion.div>
        </section>

        {/* CTA 1 */}
        <div className="text-center py-8" data-id="element-44">
          <button onClick={scrollToPurchase} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-full text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center mx-auto gap-2" data-id="element-45">
            Quiero Calmar Mi Mente Ahora
            <ArrowRightIcon className="w-5 h-5" data-id="element-46" />
          </button>
        </div>

        {/* The Mistake Section */}
        <section className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-47">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-48">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-49">
              El error que casi todas cometemos
            </h2>
            <p className="text-lg text-gray-700 mb-6" data-id="element-50">
              Nos enseñaron que la ansiedad se calma:
            </p>
            <ul className="space-y-3 mb-8" data-id="element-51">
              {['Pensando positivo', 'Respirando “bien”', 'Analizando el pasado', 'Aguantando', 'Forzándonos a estar bien'].map((item, i) => <li key={i} className="flex items-center text-gray-600" data-id="element-52">
                  <span className="w-2 h-2 bg-red-400 rounded-full mr-3" data-id="element-53"></span>
                  {item}
                </li>)}
            </ul>
            <p className="text-lg font-medium text-gray-800 mb-4" data-id="element-54">
              Pero eso es como intentar apagar una alarma gritándole que se
              calle.
            </p>
            <p className="text-2xl font-bold text-center text-red-500 my-6" data-id="element-55">
              No funciona.
            </p>
            <p className="text-gray-700" data-id="element-56">
              Porque la ansiedad no vive en la mente. Vive en el sistema
              nervioso.
            </p>
            <p className="text-lg font-semibold text-center mt-6 text-purple-700" data-id="element-57">
              Hasta que tu cuerpo no se sienta a salvo, tu cabeza no va a parar.
            </p>
          </motion.div>
        </section>

        {/* What If Section */}
        <section className="prose prose-lg mx-auto text-gray-700" data-id="element-58">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-59">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-60">
              ¿Y si no necesitaras arreglarte?
            </h3>
            <div className="space-y-4" data-id="element-61">
              <p data-id="element-62">
                ¿Y si no necesitaras ser más fuerte, más espiritual, más
                disciplinada?
              </p>
              <p data-id="element-63">¿Y si no necesitaras cambiar quién sos?</p>
              <p className="text-xl font-bold text-orange-600" data-id="element-64">
                ¿Y si solo necesitaras 10 minutos para enseñarle a tu cuerpo que
                el peligro ya pasó?
              </p>
            </div>
            <p className="mt-8" data-id="element-65">
              Eso es exactamente lo que vas a aprender en este libro.
            </p>
          </motion.div>
        </section>

        {/* CTA 2 */}
        <div className="text-center py-4" data-id="element-66">
          <button onClick={scrollToPurchase} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 rounded-full text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center mx-auto gap-2" data-id="element-67">
            Sí, Quiero Sentirme Mejor
            <HeartIcon className="w-5 h-5 fill-current" data-id="element-68" />
          </button>
        </div>

        {/* Product Intro */}
        <section className="bg-orange-50 p-8 md:p-12 rounded-3xl relative overflow-hidden" data-id="element-69">
          <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-y-1/2 translate-x-1/2" data-id="element-70"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 translate-y-1/2 -translate-x-1/2" data-id="element-71"></div>

          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="relative z-10 text-center" data-id="element-72">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-73">
              No Estás Rota: Estás en Modo Supervivencia
            </h2>
            <p className="text-xl text-orange-700 font-medium mb-8" data-id="element-74">
              Cómo calmar tu mente en 10 minutos cuando la ansiedad no se calla
            </p>

            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl text-left mb-8" data-id="element-75">
              <p className="text-lg text-gray-800 mb-4" data-id="element-76">
                Este no es un libro para “pensar mejor”. Es un libro para
                sentirte mejor.
              </p>
              <p className="text-gray-700" data-id="element-77">
                No te pide que luches con tu mente. Te enseña a regular tu
                cuerpo primero, para que la mente pueda descansar después.
              </p>
            </div>
          </motion.div>
        </section>

        {/* What You'll Find */}
        <section className="prose prose-lg mx-auto text-gray-700" data-id="element-78">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-79">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-80">
              Qué vas a encontrar dentro
            </h3>
            <p className="mb-4 font-medium" data-id="element-81">Vas a aprender:</p>
            <ul className="space-y-2 mb-8" data-id="element-82">
              {['Por qué tu ansiedad no es un fallo', 'Qué hacer cuando tu mente entra en espiral', 'Cómo calmarte incluso en medio de un día normal', 'Cómo dejar de vivir en alerta permanente', 'Cómo volver a sentir tu cuerpo como un lugar seguro'].map((item, i) => <li key={i} className="flex items-start" data-id="element-83">
                  <CheckIcon className="w-5 h-5 text-green-500 mt-1 mr-3 flex-shrink-0" data-id="element-84" />
                  <span data-id="element-85">{item}</span>
                </li>)}
            </ul>

            <p className="mb-4 font-medium" data-id="element-86">Todo con:</p>
            <ul className="space-y-2" data-id="element-87">
              {['Protocolos de 10 minutos', 'Ejercicios simples cuerpo–mente', 'Lenguaje humano, claro y sin tecnicismos', 'Aplicable incluso si nunca hiciste terapia'].map((item, i) => <li key={i} className="flex items-start" data-id="element-88">
                  <CheckIcon className="w-5 h-5 text-purple-500 mt-1 mr-3 flex-shrink-0" data-id="element-89" />
                  <span data-id="element-90">{item}</span>
                </li>)}
            </ul>
            <p className="mt-6 text-center font-medium italic text-gray-600" data-id="element-91">
              Nada místico. Nada clínico. Nada imposible de sostener.
            </p>
          </motion.div>
        </section>

        {/* Why It's Different */}
        <section className="bg-purple-50 p-8 rounded-2xl" data-id="element-92">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-93">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-94">
              Por qué esto es diferente
            </h3>
            <p className="mb-4" data-id="element-95">
              La mayoría de las soluciones intentan cambiar tus pensamientos.
            </p>
            <p className="font-bold mb-4" data-id="element-96">Este libro hace algo distinto:</p>
            <div className="bg-white p-6 rounded-xl shadow-sm text-center mb-6" data-id="element-97">
              <p className="text-xl font-medium text-purple-800" data-id="element-98">
                Primero calma el cuerpo. Después, la mente.
              </p>
            </div>
            <p data-id="element-99">
              Porque cuando el sistema nervioso sale del modo supervivencia, la
              ansiedad baja sola.
            </p>
            <p className="mt-4 font-medium" data-id="element-100">
              No por fuerza de voluntad. No por disciplina. Sino porque el
              cuerpo aprende que ya no hay peligro.
            </p>
          </motion.div>
        </section>

        {/* Origin Story */}
        <section className="prose prose-lg mx-auto text-gray-700" data-id="element-101">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-102">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-103">
              Este libro nació de la experiencia, no de una teoría
            </h3>
            <p data-id="element-104">
              Nació de un cuerpo agotado. De noches con el pecho apretado. De
              mañanas con miedo sin nombre. De intentar “hacer todo bien” y
              seguir sintiéndose mal.
            </p>
            <p data-id="element-105">
              El día que entendí que no estaba rota, que mi cuerpo solo estaba
              protegiéndome…
            </p>
            <p className="text-xl font-bold text-center my-6" data-id="element-106">todo cambió.</p>
            <p data-id="element-107">
              Y este libro es eso: el mapa que me hubiese gustado tener cuando
              creía que el problema era yo.
            </p>
            <p className="mt-8" data-id="element-108">
              Si hoy te sentís cansada de luchar, si descansar te da culpa, si
              sentís que ya probaste todo y seguís igual…
            </p>
            <p className="text-lg font-medium text-orange-700 mt-4" data-id="element-109">
              no es porque estés fallando.
            </p>
            <p className="font-bold" data-id="element-110">
              Es porque nadie te enseñó cómo salir del modo supervivencia.
            </p>
          </motion.div>
        </section>

        {/* Checklist */}
        <section className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-orange-400" data-id="element-111">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-112">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center" data-id="element-113">
              Esto es para vos si:
            </h3>
            <ul className="space-y-4" data-id="element-114">
              {CHECKLIST_ITEMS.map((item, i) => <li key={i} className="flex items-center text-lg text-gray-700" data-id="element-115">
                  <div className="bg-orange-100 p-1 rounded-full mr-4 flex-shrink-0" data-id="element-116">
                    <CheckIcon className="w-5 h-5 text-orange-600" data-id="element-117" />
                  </div>
                  {item}
                </li>)}
            </ul>
          </motion.div>
        </section>

        {/* Bonuses Section */}
        <section className="py-8" data-id="element-118">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-119">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-2" data-id="element-120">
              Bonos Exclusivos
            </h2>
            <p className="text-center text-orange-600 font-bold mb-8 uppercase tracking-wide" data-id="element-121">
              Gratis por el día de hoy
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-122">
              {[IMAGES.bonus1, IMAGES.bonus2, IMAGES.bonus3, IMAGES.bonus4].map((img, index) => <div key={index} className="relative group cursor-pointer" onClick={scrollToPurchase} data-id="element-123">
                    <div className="absolute top-4 right-4 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg z-10 animate-pulse" data-id="element-124">
                      GRATIS HOY
                    </div>
                    <img src={img} alt={`Bono ${index + 1}`} className="w-full rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 transform group-hover:-translate-y-1" data-id="element-125" />
                  </div>)}
            </div>
          </motion.div>
        </section>

        {/* Testimonials */}
        <section className="py-8" data-id="element-126">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-127">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-128">
              Lo que dicen quienes ya lo leyeron
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-129">
              {TESTIMONIALS.map((testimonial, i) => <div key={i} className={i >= 3 ? 'lg:col-span-1.5 md:col-span-1' : ''} data-id="element-130">
                  <TestimonialCard {...testimonial} data-id="element-131" />
                </div>)}
            </div>
          </motion.div>
        </section>

        {/* Purchase Section */}
        <section id="purchase" className="bg-gradient-to-br from-orange-100 to-purple-100 p-8 md:p-12 rounded-3xl text-center shadow-xl border border-white/50" data-id="element-132">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.5
        }} viewport={{
          once: true
        }} data-id="element-133">
            <img src={IMAGES.main} alt="Portada del libro" className="w-48 md:w-64 mx-auto rounded-lg shadow-2xl mb-8 transform -rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-134" />

            <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-135">
              No Estás Rota: Estás en Modo Supervivencia
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-136">
              Acceso inmediato + 4 Bonos de Regalo
            </p>

            <div className="mb-8" data-id="element-137">
              <span className="text-gray-400 line-through text-xl mr-3" data-id="element-138">
                $47.00
              </span>
              <span className="text-5xl font-bold text-gray-900" data-id="element-139">$9.97</span>
              <span className="text-gray-500 text-lg ml-1" data-id="element-140">USD</span>
            </div>

            <a href="Link de tu checkout" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-full md:w-auto bg-green-600 hover:bg-green-700 text-white font-bold py-5 px-12 rounded-full text-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 gap-3" data-id="element-141">
              Descargar Ahora
              <ArrowRightIcon className="w-6 h-6" data-id="element-142" />
            </a>

            <div className="mt-6 flex items-center justify-center gap-4 text-sm text-gray-500" data-id="element-143">
              <span className="flex items-center" data-id="element-144">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2" data-id="element-145"></div>
                Pago Seguro
              </span>
              <span className="flex items-center" data-id="element-146">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2" data-id="element-147"></div>
                Entrega Inmediata
              </span>
            </div>
          </motion.div>
        </section>

        {/* Final Closing */}
        <footer className="text-center pt-8 pb-16" data-id="element-148">
          <p className="text-xl font-medium text-gray-800 mb-2" data-id="element-149">
            👉 Descargá ahora No Estás Rota: Estás en Modo Supervivencia
          </p>
          <p className="text-xl font-medium text-gray-800 mb-2" data-id="element-150">
            👉 Empezá hoy
          </p>
          <p className="text-xl font-medium text-gray-800 mb-12" data-id="element-151">
            👉 Regalale 10 minutos de calma a tu cuerpo
          </p>

          <p className="text-lg text-gray-600" data-id="element-152">
            No porque estés fallando. Sino porque ya sobreviviste suficiente. 💛
          </p>
        </footer>
      </main>
    </div>;
}